import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/app/SEO";

export default function MentionsLegalesPage() {
  const [legal, setLegal] = useState(null);
  useEffect(() => { api.get("/config/legal").then(({ data }) => setLegal(data)).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 space-y-8">
      <SEO title="Mentions légales" description="Mentions légales de Résiliation Express (SASU) — éditeur, siège social, hébergeur, propriété intellectuelle." canonical="/mentions-legales" />
      <header>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Mentions légales</h1>
        <p className="text-sm text-muted-foreground">Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</p>
      </header>

      <section>
        <h2 className="text-xl font-semibold mb-3">Éditeur du site</h2>
        <ul className="text-sm space-y-1">
          <li><strong>Dénomination :</strong> Résiliation Express</li>
          <li><strong>Forme juridique :</strong> Société par Actions Simplifiée Unipersonnelle (SASU)</li>
          <li><strong>Capital social :</strong> 100 €</li>
          <li><strong>Siège social :</strong> 60 rue François 1er, 75008 Paris, France</li>
          <li><strong>SIREN :</strong> 107 406 878</li>
          <li><strong>Immatriculation :</strong> RCS Paris</li>
          <li><strong>EUID :</strong> FR7501.107406878</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Directeur de la publication</h2>
        <p className="text-sm">Leger Haubert, Président de Résiliation Express (SASU).</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Contact</h2>
        <p className="text-sm">Email : {legal?.privacy_contact_email || "contact@resiliationexpresse.fr"}<br />
        Formulaire : <a href="/contact" className="text-primary">page contact</a></p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Hébergeur</h2>
        <div className="text-sm space-y-1">
          <p><strong>{legal?.hosting?.name || "Emergent Labs Inc."}</strong></p>
          <p>{legal?.hosting?.address || "Infrastructure cloud Kubernetes (Union européenne)"}</p>
          {legal?.hosting?.phone && <p>Téléphone : {legal.hosting.phone}</p>}
          {legal?.hosting?.website && <p>Site : <a href={legal.hosting.website} target="_blank" rel="noopener noreferrer" className="text-primary">{legal.hosting.website}</a></p>}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Propriété intellectuelle</h2>
        <p className="text-sm leading-relaxed">Le site, la marque « Résiliation Express », ses textes, éléments graphiques, logiciel, bases de données et modèles créés par la société sont protégés par le droit d'auteur et les droits sui generis des bases de données. Toute reproduction ou représentation, totale ou partielle, sans autorisation écrite préalable de Résiliation Express est interdite.</p>
        <p className="text-sm leading-relaxed mt-3">Les marques, dénominations sociales, logos et signes distinctifs des organismes référencés dans notre annuaire restent la propriété exclusive de leurs titulaires respectifs. Leur affichage n'implique aucune affiliation ni partenariat, sauf indication expresse contraire sur la fiche concernée.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Indépendance</h2>
        <p className="text-sm leading-relaxed rounded-md border border-border p-4 bg-secondary/40">
          <strong>Résiliation Express est un service indépendant d'accompagnement administratif.</strong> Résiliation Express n'est ni une compagnie d'assurance, ni un organisme public, ni une filiale ou un représentant des entreprises référencées sur le site, sauf indication expresse contraire.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Responsabilité</h2>
        <p className="text-sm leading-relaxed">Résiliation Express fournit une prestation d'accompagnement administratif : identification des coordonnées de résiliation, génération d'un courrier personnalisé et, sur demande payante, envoi postal en recommandé. Nous ne rendons pas de consultation juridique personnalisée et ne pouvons garantir l'acceptation d'une résiliation par un organisme tiers, qui reste seul décisionnaire.</p>
        <p className="text-sm leading-relaxed mt-3">Résiliation Express met en œuvre les meilleurs efforts pour vérifier l'exactitude des informations affichées, sans toutefois pouvoir garantir leur exhaustivité ou leur mise à jour permanente. La présente clause ne prive le consommateur d'aucun droit que la loi lui reconnaît.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Liens externes</h2>
        <p className="text-sm leading-relaxed">Les liens vers des sites tiers présents sur Résiliation Express sont fournis à titre informatif. Leur contenu relève de la responsabilité exclusive de leurs éditeurs.</p>
      </section>

      <section id="mediateur">
        <h2 className="text-xl font-semibold mb-3">Médiateur de la consommation</h2>
        <p className="text-sm leading-relaxed">
          Conformément aux articles L. 612-1 et suivants du Code de la consommation, en cas de litige non résolu après une réclamation
          écrite préalable auprès de Résiliation Express (<a href="mailto:contact@resiliationexpresse.fr" className="text-primary">contact@resiliationexpresse.fr</a>
          ou <a href="/reclamation" className="text-primary">/reclamation</a>), le consommateur peut recourir gratuitement au médiateur
          de la consommation dont nous relevons : <strong>Société Médiation Professionnelle</strong>.
        </p>
        <div className="text-sm mt-3 rounded-md border border-border p-4 bg-secondary/40" data-testid="mediateur-smp">
          <p><strong>Société Médiation Professionnelle</strong></p>
          <p className="mt-1">Alteritae, 5 rue Salvaing, 12000 Rodez</p>
          <p className="mt-1">Site officiel : <a href="https://www.mediateur-consommation-smp.fr/" target="_blank" rel="noopener noreferrer" className="text-primary">https://www.mediateur-consommation-smp.fr/</a></p>
          <p className="mt-2 text-muted-foreground">La saisine peut être effectuée en ligne via le site officiel ou par courrier postal à l'adresse ci-dessus. Le recours au médiateur est gratuit pour le consommateur.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">Signalement</h2>
        <p className="text-sm leading-relaxed">Pour signaler une information incorrecte, une adresse de résiliation obsolète, une atteinte à une marque, un contenu illicite ou une faille de sécurité, écrivez à <a href="mailto:contact@resiliationexpresse.fr" className="text-primary">contact@resiliationexpresse.fr</a> ou utilisez la page <a href="/reclamation" className="text-primary">/reclamation</a>. Nous accusons réception sous 48h ouvrées.</p>
      </section>
    </div>
  );
}
